import { useMemo } from "react";
import { Box, Divider, useTheme } from "@mui/material";
import CustomTypography from "../../common/form/CustomTypography";
import ImageUpload from "../../common/upload/ImageUpload";
import AppButton from "../../common/buttons/AppButton";
import FormTextField from "../../common/form/FormTextField";
import { ImageData } from "../../../../models/Image/Image";
import { useStores } from "../../../../use-stores";
import { observer } from "mobx-react";
import { EmployeeRoles } from "../../../../models/Employees/EmployeeRoles";

const LoginPageSettings = ({
  onSaveChanges,
  onSettingChange,
}: {
  onSettingChange: (key: string, value: any) => void;
  onSaveChanges: () => void;
}) => {
  const theme = useTheme();
  const { swagStoreStore, userStore } = useStores();

  const existingImages = useMemo<ImageData[]>(() => {
    if (typeof swagStoreStore.settings.loginPage.loginPageImage === "string") {
      return [
        {
          img: swagStoreStore.settings.loginPage.loginPageImage,
          title: "Login Page Image",
        },
      ];
    }
    return [];
  }, [swagStoreStore.settings.loginPage.loginPageImage]);

  const newImages = useMemo<File[]>(() => {
    if (swagStoreStore.settings.loginPage.loginPageImage instanceof File) {
      return [swagStoreStore.settings.loginPage.loginPageImage];
    }
    return [];
  }, [swagStoreStore.settings.loginPage.loginPageImage]);

  return (
    <>
      <Box>
        <CustomTypography
          variant="heading6"
          fontWeight={600}
          style={{ marginBottom: "12px" }}
        >
          Login page setting
        </CustomTypography>
        <CustomTypography
          variant="heading9"
          color={theme.palette.grey[600]}
          style={{ marginBottom: "12px" }}
        >
          Customize your login page.
        </CustomTypography>

        <Divider sx={{ my: 3, width: "100%" }} />

        <Box
          sx={{
            boxShadow: `${theme.palette.customColors.shadowVeryLight} 0px 6px 24px 0px, ${theme.palette.customColors.shadowLightBorder} 0px 0px 0px 1px`,
            p: 2.2,
            borderRadius: 2,
          }}
        >
          <CustomTypography
            variant="body1"
            fontWeight={600}
            style={{ marginBottom: "12px" }}
          >
            Upload Images
          </CustomTypography>

          <Box>
            <ImageUpload
              addImagesToBeUploaded={(files) => {
                if (files && files.length > 0) {
                  onSettingChange("loginPageImage", files[0]);
                }
              }}
              allowedFileTypes="image/*"
              fileInfo="PNG, JPG up to 2MB | Recommended size: 700x700 pixels"
              showImagesList={false}
              size="large"
              multiple={false}
              uploadText="Drop images or Browse"
              previewImage={true}
              minWidth="100%"
              maxWidth="232px"
              minHeight="134px"
              existingImageList={existingImages}
              newImageList={newImages}
              deleteImage={() => {
                onSettingChange("loginPageImage", null);
              }}
              removeImage={() => {
                onSettingChange("loginPageImage", null);
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            mt: 3,
            mb: 2,
            boxShadow: `${theme.palette.customColors.shadowVeryLight} 0px 6px 24px 0px, ${theme.palette.customColors.shadowLightBorder} 0px 0px 0px 1px`,
            p: 2.2,
            borderRadius: 2,
          }}
        >
          <CustomTypography
            variant="body1"
            fontWeight={600}
            style={{ marginBottom: "6px" }}
          >
            Login Page Content
          </CustomTypography>

          <CustomTypography
            variant="body3"
            fontWeight={600}
            style={{
              marginBottom: "12px",
              marginTop: "14px",
              color: theme.palette.customColors?.greyTextColor,
            }}
          >
            Heading text
          </CustomTypography>

          <Box>
            <FormTextField
              label=""
              placeholder="Enter heading text"
              value={swagStoreStore.settings.loginPage.headingText || ""}
              onChange={(id, value) =>
                onSettingChange("headingText", value || "")
              }
              sx={{ marginBottom: "6px" }}
              backgroundColor={theme.palette.customColors.grayBackGroundColor}
              borderColor={theme.palette.customColors.greyBorder}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <CustomTypography
              variant="body3"
              fontWeight={600}
              style={{
                marginBottom: "12px",
                marginTop: "12px",
                color: theme.palette.customColors?.greyTextColor,
              }}
            >
              Subheading text
            </CustomTypography>

            <FormTextField
              label=""
              placeholder="Enter your registered email address, and we'll send you a OTP to log in securely"
              value={swagStoreStore.settings.loginPage.subheadingText || ""}
              onChange={(id, value) =>
                onSettingChange("subheadingText", value || "")
              }
              sx={{ marginBottom: "6px" }}
              backgroundColor={theme.palette.customColors.grayBackGroundColor}
              borderColor={theme.palette.customColors.greyBorder}
            />
          </Box>
        </Box>
      </Box>
      <Box>
        <AppButton
          label="Save Changes"
          onClick={onSaveChanges}
          customStyles={{
            mb: 10,
            width: "100%",
            backgroundColor: theme.palette.customColors?.darkBlackBorder,
            color: theme.palette.customColors?.white,
            "&:hover": {
              backgroundColor: theme.palette.customColors?.darkBlackBorder,
              color: theme.palette.customColors?.white,
            },
            "&:disabled": {
              backgroundColor: theme.palette.customColors?.greyBorder,
              color: theme.palette.customColors?.white,
            },
          }}
          variant="primaryBlock"
          disabled={!userStore.hasPermission(EmployeeRoles.EDIT_SWAGSTORE)}
        />
      </Box>
    </>
  );
};

export default observer(LoginPageSettings);
